import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { Task, TaskAPI, TaskUpdatePhasePayload } from 'api/tasks';
import { saveTaskDetailSaga } from 'data/tasks/actions';
import { ApiErrorData } from 'queries/error';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  ProjectTasksQueryKey,
} from './common';
import { tenMinutes } from './constants';

type Tasks = Task[];

export const useProjectTasksQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Tasks, unknown, Tasks, ProjectTasksQueryKey> = {},
): UseQueryResult<Tasks> => {
  return useQuery(
    getProjectTasksQueryKey(projectId),
    () => TaskAPI.getTaskList(projectId[1]),
    {
      staleTime: tenMinutes,
      ...options,
    },
  );
};

type TaskDetailQueryKey = [string, number, number];
const getTaskDetailQueryKey = ([
  ,
  projectId,
  taskId,
]: TaskId): TaskDetailQueryKey => ['task-detail', projectId, taskId];

const useUpdatePhaseTaskMutation = (
  taskId: number,
): UseMutationResult<Task, unknown, TaskUpdatePhasePayload> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<Task, unknown, TaskUpdatePhasePayload>(
    (payload) => TaskAPI.updatePhase(taskId, payload),
    {
      onSuccess: async (task) => {
        const {
          project: { projectId },
        } = task;

        dispatch(saveTaskDetailSaga(task));

        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );

        await queryClient.setQueryData(
          getTaskDetailQueryKey(TaskId(projectId, taskId)),
          task,
        );
        await queryClient.invalidateQueries(
          getProjectDependencyMapKey(projectId),
        );
      },
    },
  );
};

const useTaskDetailQuery = (
  taskId: TaskId,
  queryOptions: UseQueryOptions<
    Task,
    ApiErrorData,
    Task,
    TaskDetailQueryKey
  > = {},
): UseQueryResult<Task, ApiErrorData> => {
  return useQuery(
    getTaskDetailQueryKey(taskId),
    () => TaskAPI.getTask(taskId[1], taskId[2]),
    {
      staleTime: tenMinutes,
      retry: 2,
      ...queryOptions,
    },
  );
};

export {
  useUpdatePhaseTaskMutation,
  getTaskDetailQueryKey,
  useTaskDetailQuery,
};
